/**
 * CORE-BN — Front-of-House Attention Capacity · shop policy
 *
 * Constitutional authority: CORE-BN, Definition of Done #3 — "Claims are
 * weighted, with conservative defaults and shop-level tuning through
 * CORE-BI."
 *
 * Where policy lives
 * ------------------
 * CORE-BI shop culture is already persisted as `shop_profile.shop_culture`
 * (a JSON object holding the shop's tunable operating preferences —
 * `willingness_to_reassign`, `no_show_grace_threshold_min`,
 * `outdoorWeatherSettings`, and so on). CORE-BN's policy is a nested key
 * on that same object:
 *
 *     shop_profile.shop_culture.foh_attention_policy
 *
 * This is deliberate. Creating a second shop-policy table would create
 * duplicate authority over shop tuning, which the constitution's
 * Dependencies section places squarely with CORE-BI. No migration is
 * required, and a shop that has never tuned anything simply has no key.
 *
 * Conservative defaults
 * ---------------------
 * "Conservative" here means: err toward reporting saturation EARLIER, not
 * later. A model that under-reports saturation returns the shop to
 * today's failure — planning a morning the front of house cannot deliver.
 * So the available-attention denominator is discounted (nobody is 100%
 * available), and unobserved demand is never assumed to be zero-cost.
 *
 * Safety
 * ------
 * Every shop-supplied value is bounded, and the bounds are enforced, not
 * merely documented. A shop cannot configure its way to infinite
 * front-of-house attention, cannot zero out the cost of any meaningful
 * claim, cannot turn dwell escalation into a blanket custody discount,
 * and cannot invert the saturation bands. Out-of-range and malformed
 * values fall back to the default and are reported as `'default'`, so an
 * unsafe policy degrades to the conservative model rather than to a
 * fabricated one.
 *
 * The one claim with no configurable per-occurrence weight is
 * `waiter_present`: lobby presence is continuous, so its cost is derived
 * from `waiter_presence_window_fraction` and its discrete weight is
 * never read. A shop-supplied value for it is ignored rather than
 * silently stored, so nobody can tune a number that does nothing.
 *
 * Purity: no I/O, no clock, no React. The caller supplies the raw JSON.
 */

import {
  FOH_ATTENTION_CLAIM_KINDS,
  type FohAppliedPolicySnapshot,
  type FohAttentionClaimKind,
  type FohDwellBucket,
  type FohDwellDistribution,
  type FohWeightSource,
} from './types';

// =============================================================================
// The shop_culture key
// =============================================================================

/** The single key CORE-BN reads from (and only from) CORE-BI shop culture. */
export const FOH_ATTENTION_POLICY_CULTURE_KEY = 'foh_attention_policy';

// =============================================================================
// Defaults
// =============================================================================

/**
 * Default attention cost, in attention-minutes, of ONE occurrence of each
 * claim.
 *
 * These are estimates of human cost, not measurements, and the model
 * says so on every result. The relative ordering is the part that carries
 * doctrine:
 *
 *   - A decision or approval is the most expensive discrete claim: it
 *     cannot be delegated, interrupted cheaply, or done while walking.
 *   - An arrival is expensive: greet, verify, gather concern, set
 *     expectation.
 *   - A call or message is moderate.
 *   - A ticket on the desk costs more than one merely waiting to be
 *     accepted, because it is active work rather than a queue entry —
 *     but the waiting one escalates with dwell, which is where the
 *     shop's real pain lives.
 *   - A ticket kicked back to a technician is the cheapest custody claim
 *     by a wide margin: the technician is the actor.
 *   - `waiter_present` is NOT a per-occurrence cost. Lobby presence is
 *     continuous, so its cost is derived from the window length via
 *     `waiterPresenceWindowFraction` and this value is unused. It is
 *     present in the record only so the vocabulary is total.
 *   - `technician_support` carries a real default cost so that a shop
 *     which later begins observing it does not get a free claim; today
 *     it is normally reported `'unobserved'` and contributes nothing.
 */
export const DEFAULT_FOH_CLAIM_WEIGHT_MINUTES: Readonly<
  Record<FohAttentionClaimKind, number>
> = Object.freeze({
  arrival_intake: 6,
  waiter_present: 0,
  custody_pending_accept: 3,
  custody_with_advisor: 4,
  custody_pending_tech_accept: 1.5,
  customer_communication: 4,
  decision_approval: 5,
  technician_support: 4,
});

/**
 * Default dwell escalation multipliers for custody claims.
 *
 * Doctrine (CORE-BN-GAP-003): the shop already ages custody dwell at
 * 5 and 10 minutes and then throws the result away at the row's styling.
 * An aged custody item is not the same claim as a fresh one — it is a
 * claim the shop has already demonstrated it cannot reach, and reaching
 * it now costs more (the customer has been waiting, the technician is
 * blocked, the context has gone cold). The escalation is what makes
 * "three urgent rows" differ operationally from "one urgent row".
 */
export const DEFAULT_FOH_DWELL_ESCALATION: FohDwellDistribution = Object.freeze({
  normal: 1,
  warning: 1.5,
  urgent: 2.5,
});

/**
 * Default saturation thresholds, expressed as utilization
 * (consumed / available).
 *
 * Band assignment is half-open and lower-inclusive at each boundary, so a
 * threshold value belongs to the WORSE band:
 *
 *   comfortable   : utilization <  0.70
 *   loaded        : 0.70 <= utilization < 0.90
 *   saturated     : 0.90 <= utilization < 1.00
 *   over_capacity : 1.00 <= utilization
 *
 * `loaded` at 0.70 rather than 0.85 is the conservative choice: the
 * constitution requires saturation to be visible BEFORE it produces
 * delay, which means the first warning has to arrive while there is
 * still headroom to act on it.
 */
export const DEFAULT_FOH_SATURATION_THRESHOLDS: Readonly<{
  loaded: number;
  saturated: number;
  overCapacity: number;
}> = Object.freeze({
  loaded: 0.7,
  saturated: 0.9,
  overCapacity: 1.0,
});

/**
 * Fraction of a window one front-of-house person can actually spend on
 * modelled claims.
 *
 * Not 1.0, and deliberately so. A person on the desk also walks the lot,
 * files, restocks, takes a break, and absorbs the interruptions this
 * model does not enumerate. Budgeting 100% of every person would produce
 * a model that reports comfort right up until the shop falls over.
 */
export const DEFAULT_FOH_PERSON_EFFECTIVE_ATTENTION_FRACTION = 0.75;

/**
 * Fraction of one person's window that a single waiting customer claims.
 *
 * Lobby presence is the one claim with no discrete occurrence to count.
 * A customer in the lobby is checked on, updated, and — most expensively
 * — kept in mind. 0.15 means two waiters consume roughly a third of one
 * person for the window, which matches the constitutional scenario where
 * two waiters plus eleven expected arrivals saturate a two-person desk.
 */
export const DEFAULT_FOH_WAITER_PRESENCE_WINDOW_FRACTION = 0.15;

/** Default estimate window: the next half hour, per the constitution's scenario. */
export const DEFAULT_FOH_WINDOW_MINUTES = 30;

// =============================================================================
// Bounds
// =============================================================================

/**
 * Hard bounds on shop-supplied values. A value outside its bound is not
 * clamped silently — it is REJECTED and the default is used, and the
 * result reports `'default'`. Silent clamping would let a shop believe it
 * had tuned something it had not.
 */
export const FOH_POLICY_BOUNDS = Object.freeze({
  /**
   * A meaningful claim may be cheap, but it may never be FREE.
   *
   * The floor is not cosmetic. A shop that could set `arrival_intake` to
   * 0 could delete the single largest demand term from its own capacity
   * model and be reported `comfortable` through a morning that buries it
   * — and because weights are shop-attributed and disclosed, it would
   * look like a deliberate, legitimate configuration. That is a capacity
   * model configured into decoration.
   *
   * The constitution allows a shop to tune how much a claim costs. It
   * does not contemplate a shop declaring that greeting a customer, or
   * making an approval decision, consumes no attention at all. 0.25 min
   * (15 seconds) is low enough to express "this barely costs us
   * anything" and high enough that eight of them still register.
   *
   * `waiter_present` is exempt from this record entirely — see
   * `isShopConfigurableClaimWeight`.
   */
  claimWeightMinutes: Object.freeze({ min: 0.25, max: 30 }),
  /**
   * Dwell escalation is an ESCALATION. It may leave an aged claim at
   * parity with a fresh one, but it may never discount it.
   *
   * A floor of 1.0 (rather than 0.5) closes the same hole from the other
   * side: with a sub-1 floor a shop could set every bucket to 0.5 and
   * halve the cost of its entire custody backlog without touching a
   * single claim weight — a blanket discount wearing an escalation
   * curve's clothes. The base cost of a custody claim is its claim
   * weight, which is already tunable and already floored.
   */
  dwellEscalation: Object.freeze({ min: 1, max: 5 }),
  /**
   * Thresholds must stay inside a sane window AND strictly ascending.
   * A shop cannot push `loaded` past 2.0 and pretend to be comfortable
   * at twice its capacity.
   */
  saturationThreshold: Object.freeze({ min: 0.1, max: 2 }),
  /**
   * A person is never more than fully available and never less than a
   * quarter available. `max: 1` is the constitutional ceiling: the model
   * must not be tunable into inventing attention that does not exist.
   */
  personEffectiveAttentionFraction: Object.freeze({ min: 0.25, max: 1 }),
  /**
   * A single waiter cannot consume more than one whole person, and
   * cannot consume nothing.
   *
   * The floor mirrors `claimWeightMinutes`: this fraction IS the lobby
   * claim's cost, so a zero here would delete a constitutional claim —
   * "present customers claim attention continuously" — by configuration.
   * 0.01 of a 30-minute window is under 20 seconds per waiter, which is
   * as close to free as an honest model should let a shop get.
   */
  waiterPresenceWindowFraction: Object.freeze({ min: 0.01, max: 1 }),
  /** Windows shorter than 5 minutes are noise; longer than 8 hours are not a window. */
  windowMinutes: Object.freeze({ min: 5, max: 480 }),
  /**
   * A stated front-of-house headcount override.
   *
   * `min: 0` is deliberate and is NOT the zero-cost hole closed above.
   * Zero here means "we have no dedicated front-of-house staff", which
   * drives the model to `'unknown'` — the safe direction. A shop can opt
   * out of the estimate; it cannot opt out of the estimate's honesty.
   */
  staffHeadcountOverride: Object.freeze({ min: 0, max: 50 }),
});

// =============================================================================
// Resolved policy
// =============================================================================

export interface FohAttentionPolicy {
  claimWeightMinutes: Readonly<Record<FohAttentionClaimKind, number>>;
  claimWeightSources: Readonly<Record<FohAttentionClaimKind, FohWeightSource>>;
  dwellEscalationMultipliers: FohDwellDistribution;
  dwellEscalationSource: FohWeightSource;
  saturationThresholds: Readonly<{
    loaded: number;
    saturated: number;
    overCapacity: number;
  }>;
  saturationThresholdsSource: FohWeightSource;
  personEffectiveAttentionFraction: number;
  personEffectiveAttentionFractionSource: FohWeightSource;
  waiterPresenceWindowFraction: number;
  waiterPresenceWindowFractionSource: FohWeightSource;
  windowMinutes: number;
  windowMinutesSource: FohWeightSource;
  /**
   * Shop-stated front-of-house headcount. `null` when not configured, in
   * which case staffing is observed rather than declared.
   */
  staffHeadcountOverride: number | null;
  /** True when no field was shop-configured — the untuned default path. */
  usingAllDefaults: boolean;
}

// =============================================================================
// Parsing
// =============================================================================

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Claim kinds whose per-occurrence weight is meaningful and therefore
 * shop-configurable.
 *
 * `waiter_present` is excluded: its cost comes from
 * `waiter_presence_window_fraction` because lobby presence is continuous
 * rather than discrete, so its entry in the weight record is never read
 * by the model. Accepting a shop value for it would let someone tune a
 * number that changes nothing and believe they had changed something.
 */
export function isShopConfigurableClaimWeight(
  kind: FohAttentionClaimKind,
): boolean {
  return kind !== 'waiter_present';
}

/**
 * The claim kinds a settings surface should offer, in the order a person
 * would reason about them. Excludes `waiter_present` for the reason
 * above — the lobby is tuned with its own control.
 */
export const FOH_CONFIGURABLE_CLAIM_KINDS: ReadonlyArray<FohAttentionClaimKind> =
  Object.freeze(
    FOH_ATTENTION_CLAIM_KINDS.filter(isShopConfigurableClaimWeight),
  );

/**
 * Accept a finite number inside `[min, max]`. Anything else — string,
 * NaN, Infinity, null, out of range — returns `null`, which means "use
 * the default".
 */
function boundedNumber(
  value: unknown,
  bounds: Readonly<{ min: number; max: number }>,
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value < bounds.min || value > bounds.max) return null;
  return value;
}

/**
 * Read `shop_culture.foh_attention_policy` out of a raw shop-culture
 * blob. Tolerates every shape a JSON column can hold, because the column
 * is untyped and a malformed policy must degrade to defaults rather than
 * throw on a page that is trying to tell someone the shop is drowning.
 */
export function readFohAttentionPolicyBlob(shopCulture: unknown): unknown {
  if (!isPlainObject(shopCulture)) return null;
  return shopCulture[FOH_ATTENTION_POLICY_CULTURE_KEY] ?? null;
}

/**
 * Resolve the effective policy from a raw `foh_attention_policy` blob.
 *
 * Every field independently falls back to its conservative default, and
 * every field independently reports whether the value in force came from
 * the shop or from the default. Partial configuration is normal and
 * supported: a shop that only tunes `window_minutes` keeps default
 * weights, and the result says so.
 */
export function resolveFohAttentionPolicy(raw: unknown): FohAttentionPolicy {
  const blob = isPlainObject(raw) ? raw : null;

  // --- claim weights ---------------------------------------------------
  const rawWeights = blob && isPlainObject(blob.claim_weight_minutes)
    ? blob.claim_weight_minutes
    : null;
  const claimWeightMinutes = {} as Record<FohAttentionClaimKind, number>;
  const claimWeightSources = {} as Record<FohAttentionClaimKind, FohWeightSource>;
  for (const kind of FOH_ATTENTION_CLAIM_KINDS) {
    const supplied =
      rawWeights && isShopConfigurableClaimWeight(kind)
        ? boundedNumber(rawWeights[kind], FOH_POLICY_BOUNDS.claimWeightMinutes)
        : null;
    claimWeightMinutes[kind] = supplied ?? DEFAULT_FOH_CLAIM_WEIGHT_MINUTES[kind];
    claimWeightSources[kind] = supplied === null ? 'default' : 'shop';
  }

  // --- dwell escalation ------------------------------------------------
  // All-or-nothing: a partially-specified escalation table is a shop
  // saying something it has not thought through, and mixing two tables
  // would produce a curve neither the shop nor the default intended.
  const rawDwell = blob && isPlainObject(blob.dwell_escalation)
    ? blob.dwell_escalation
    : null;
  let dwellEscalationMultipliers = DEFAULT_FOH_DWELL_ESCALATION;
  let dwellEscalationSource: FohWeightSource = 'default';
  if (rawDwell) {
    const buckets: FohDwellBucket[] = ['normal', 'warning', 'urgent'];
    const parsed: Partial<Record<FohDwellBucket, number>> = {};
    let allPresent = true;
    for (const b of buckets) {
      const v = boundedNumber(rawDwell[b], FOH_POLICY_BOUNDS.dwellEscalation);
      if (v === null) {
        allPresent = false;
        break;
      }
      parsed[b] = v;
    }
    // Escalation must not decrease with age. A shop declaring that an
    // urgent item costs LESS than a fresh one has inverted the doctrine,
    // and the model refuses rather than obeys.
    const monotonic =
      allPresent &&
      parsed.normal !== undefined &&
      parsed.warning !== undefined &&
      parsed.urgent !== undefined &&
      parsed.normal <= parsed.warning &&
      parsed.warning <= parsed.urgent;
    if (monotonic) {
      dwellEscalationMultipliers = Object.freeze({
        normal: parsed.normal as number,
        warning: parsed.warning as number,
        urgent: parsed.urgent as number,
      });
      dwellEscalationSource = 'shop';
    }
  }

  // --- saturation thresholds -------------------------------------------
  // Also all-or-nothing and strictly ascending, for the same reason.
  const rawThresholds = blob && isPlainObject(blob.saturation_thresholds)
    ? blob.saturation_thresholds
    : null;
  let saturationThresholds = DEFAULT_FOH_SATURATION_THRESHOLDS;
  let saturationThresholdsSource: FohWeightSource = 'default';
  if (rawThresholds) {
    const loaded = boundedNumber(
      rawThresholds.loaded,
      FOH_POLICY_BOUNDS.saturationThreshold,
    );
    const saturated = boundedNumber(
      rawThresholds.saturated,
      FOH_POLICY_BOUNDS.saturationThreshold,
    );
    const overCapacity = boundedNumber(
      rawThresholds.over_capacity,
      FOH_POLICY_BOUNDS.saturationThreshold,
    );
    if (
      loaded !== null &&
      saturated !== null &&
      overCapacity !== null &&
      loaded < saturated &&
      saturated < overCapacity
    ) {
      saturationThresholds = Object.freeze({ loaded, saturated, overCapacity });
      saturationThresholdsSource = 'shop';
    }
  }

  // --- scalars ----------------------------------------------------------
  const personFraction = blob
    ? boundedNumber(
        blob.person_effective_attention_fraction,
        FOH_POLICY_BOUNDS.personEffectiveAttentionFraction,
      )
    : null;
  const waiterFraction = blob
    ? boundedNumber(
        blob.waiter_presence_window_fraction,
        FOH_POLICY_BOUNDS.waiterPresenceWindowFraction,
      )
    : null;
  const windowMinutes = blob
    ? boundedNumber(blob.window_minutes, FOH_POLICY_BOUNDS.windowMinutes)
    : null;
  const staffOverrideRaw = blob
    ? boundedNumber(
        blob.foh_staff_headcount,
        FOH_POLICY_BOUNDS.staffHeadcountOverride,
      )
    : null;
  // Headcount is people, not a fraction of a person.
  const staffHeadcountOverride =
    staffOverrideRaw === null ? null : Math.floor(staffOverrideRaw);

  const anyShopConfigured =
    Object.values(claimWeightSources).some((s) => s === 'shop') ||
    dwellEscalationSource === 'shop' ||
    saturationThresholdsSource === 'shop' ||
    personFraction !== null ||
    waiterFraction !== null ||
    windowMinutes !== null ||
    staffHeadcountOverride !== null;

  return {
    claimWeightMinutes: Object.freeze(claimWeightMinutes),
    claimWeightSources: Object.freeze(claimWeightSources),
    dwellEscalationMultipliers,
    dwellEscalationSource,
    saturationThresholds,
    saturationThresholdsSource,
    personEffectiveAttentionFraction:
      personFraction ?? DEFAULT_FOH_PERSON_EFFECTIVE_ATTENTION_FRACTION,
    personEffectiveAttentionFractionSource:
      personFraction === null ? 'default' : 'shop',
    waiterPresenceWindowFraction:
      waiterFraction ?? DEFAULT_FOH_WAITER_PRESENCE_WINDOW_FRACTION,
    waiterPresenceWindowFractionSource:
      waiterFraction === null ? 'default' : 'shop',
    windowMinutes: windowMinutes ?? DEFAULT_FOH_WINDOW_MINUTES,
    windowMinutesSource: windowMinutes === null ? 'default' : 'shop',
    staffHeadcountOverride,
    usingAllDefaults: !anyShopConfigured,
  };
}

/** The untuned policy. Equivalent to a shop that has configured nothing. */
export function defaultFohAttentionPolicy(): FohAttentionPolicy {
  return resolveFohAttentionPolicy(null);
}

/**
 * Project the resolved policy into the immutable snapshot carried on a
 * result. `windowMinutes` is passed separately because the effective
 * window is whatever the caller actually measured, which may differ from
 * the configured default (e.g. a caller estimating a named future
 * window).
 */
export function toAppliedPolicySnapshot(
  policy: FohAttentionPolicy,
  effectiveWindowMinutes: number,
): FohAppliedPolicySnapshot {
  return Object.freeze({
    claimWeightMinutes: policy.claimWeightMinutes,
    claimWeightSources: policy.claimWeightSources,
    dwellEscalationMultipliers: policy.dwellEscalationMultipliers,
    dwellEscalationSource: policy.dwellEscalationSource,
    saturationThresholds: policy.saturationThresholds,
    saturationThresholdsSource: policy.saturationThresholdsSource,
    personEffectiveAttentionFraction: policy.personEffectiveAttentionFraction,
    personEffectiveAttentionFractionSource:
      policy.personEffectiveAttentionFractionSource,
    waiterPresenceWindowFraction: policy.waiterPresenceWindowFraction,
    waiterPresenceWindowFractionSource:
      policy.waiterPresenceWindowFractionSource,
    windowMinutes: effectiveWindowMinutes,
    windowMinutesSource: policy.windowMinutesSource,
    usingAllDefaults: policy.usingAllDefaults,
  });
}
